/* Draft-mode reveal panel: the The Loom gateway with a free-to-try
 * offer. Rendered as a warm parchment "editor's card" — mock chapter
 * heading, a stack of manuscript-style bullet points, and a large
 * oxblood CTA.
 *
 * Sits behind the ModeSlider and appears when the slider is dragged
 * toward "Draft Mode" (see `ModeSlider.jsx`).
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PenTool, ArrowRight, PlayCircle, Sparkles, Loader2 } from 'lucide-react';
import { P, FONTS, GRAIN_URL } from './palette';

export default function ProWriterGateway({ onDemo, demoBusy = false, interactive = true }) {
  const { t } = useTranslation();
  const Wrap = interactive ? Link : 'div';
  const wrapProps = interactive ? { to: '/app' } : {};
  return (
    <div
      className="relative flex h-full items-center justify-center px-8 py-10"
      style={{ backgroundColor: P.paper }}
    >
      {/* Ruled paper backdrop */}
      <div
        className="pointer-events-none absolute inset-0 opacity-25"
        style={{
          backgroundImage: `repeating-linear-gradient(to bottom, transparent 0, transparent 31px, ${P.gold}55 31px, ${P.gold}55 32px)`,
        }}
      />
      <div className="pointer-events-none absolute inset-0 opacity-[0.12]" style={{ backgroundImage: GRAIN_URL }} />

      <div
        className="relative w-full max-w-md rounded-lg border p-7 shadow-lg"
        data-testid="scriptorium-pro-writer-gateway"
        style={{
          backgroundColor: P.paperDeep,
          borderColor:     P.gold,
          boxShadow:       `0 22px 50px -22px ${P.leather}55`,
        }}
      >
        <div
          className="mb-3 flex flex-wrap items-center justify-between gap-x-2 gap-y-1.5 text-[9.5px] uppercase tracking-[0.22em] sm:text-[10.5px] sm:tracking-[0.28em]"
          style={{ color: P.oxblood, fontFamily: FONTS.type }}
        >
          <span>{t('landing.gatewayChapterLabel')}</span>
          <span
            className="rounded-full px-2 py-0.5"
            style={{ backgroundColor: P.oxblood, color: P.paper, fontFamily: FONTS.type }}
          >
            {t('landing.gatewayTrialBadge')}
          </span>
        </div>

        <h2
          className="text-3xl leading-[1.2]"
          style={{ fontFamily: FONTS.display, fontWeight: 700, color: P.ink }}
        >
          {t('landing.gatewayHeadline')}
        </h2>
        <p
          className="mt-3 text-[15px] leading-[1.65]"
          style={{ color: P.inkSoft, fontFamily: FONTS.body }}
        >
          {t('landing.gatewayDescription')}
        </p>

        <ul className="mt-4 space-y-2" style={{ color: P.ink, fontFamily: FONTS.body }}>
          {[
            t('landing.gatewayBullet1'),
            t('landing.gatewayBullet2'),
            t('landing.gatewayBullet3'),
            t('landing.gatewayBullet4'),
          ].map((line) => (
            <li key={line} className="flex items-start gap-2 text-[14px] leading-[1.5]">
              <span
                className="mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full"
                style={{ backgroundColor: P.gold }}
              />
              {line}
            </li>
          ))}
        </ul>

        <div className="mt-6 flex flex-wrap gap-2">
          <Wrap
            {...wrapProps}
            data-testid="scriptorium-gateway-trial"
            className="group inline-flex items-center gap-2 rounded-md px-4 py-2.5 text-[13px] font-semibold transition hover:brightness-110"
            style={{
              backgroundColor: P.oxblood,
              color:           P.paper,
              fontFamily:      FONTS.display,
            }}
          >
            <PenTool className="h-4 w-4" />
            {t('landing.gatewayCta')}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Wrap>
          {/* iter55f56 — "Demo · no signup" fallback removed. The
           * free-to-try CTA above is now the sole path into the app. */}
        </div>

        <div
          className="mt-5 flex items-center gap-2 text-[11px]"
          style={{ color: P.inkSoft }}
        >
          <Sparkles className="h-3 w-3" style={{ color: P.verdant }} />
          {t('landing.gatewayTrustline')}
        </div>
      </div>
    </div>
  );
}
